import { Badge, Button, Checkbox, Field, Input } from '@cloudflare/kumo';
import { SidePanel } from './ui/SidePanel';
import { useCallback, useEffect, useState } from 'react';
import { ProviderLogo, modelVendor } from './ProviderLogo';
import { PasswordField } from './ui/PasswordField';
import { ICONS } from './ui/icons';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';

interface Provider { id: string; label: string; needsBaseUrl: boolean; enabled: boolean; hasKey: boolean; baseUrl: string; model: string; disableReasoning: boolean; accountId?: string | null; gatewayId?: string | null }
interface Config { defaultProvider: string | null; fallbackProvider: string | null; dailyRequestsPerOrg: number; providers: Provider[] }

/** Plataforma › Integrações: provedores de LLM (copiloto e IA padrão das campanhas) */
export function AiProvidersCard() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [cfg, setCfg] = useState<Config | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string>('compatible');
  const [form, setForm] = useState({ enabled: true, baseUrl: '', apiKey: '', model: '', disableReasoning: true, isDefault: false, isFallback: false, daily: '', accountId: '', gatewayId: '' });
  const [models, setModels] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/ai/config');
    if (res.ok) setCfg(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const pick = (id: string, c = cfg) => {
    const p = c?.providers.find((x) => x.id === id);
    if (!p || !c) return;
    setEditing(id);
    setModels([]);
    setForm({ enabled: p.enabled || !p.hasKey, baseUrl: p.baseUrl, apiKey: '', model: p.model, disableReasoning: p.disableReasoning, isDefault: c.defaultProvider === id || !c.defaultProvider, isFallback: c.fallbackProvider === id, daily: String(c.dailyRequestsPerOrg), accountId: p.accountId ?? '', gatewayId: p.gatewayId ?? '' });
  };

  const current = cfg?.providers.find((p) => p.id === editing);
  const active = cfg?.providers.find((p) => p.id === cfg.defaultProvider && p.enabled);

  const req = async (key: string, url: string, init: RequestInit) => {
    setBusy(key);
    try {
      const res = await fetchApi(url, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível concluir');
      return body;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível concluir');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = await req('save', '/api/ai/config', {
      method: 'PUT',
      body: JSON.stringify({
        ...(form.isDefault ? { defaultProvider: editing } : cfg?.defaultProvider === editing ? { defaultProvider: null } : {}),
        ...(form.isFallback && !form.isDefault ? { fallbackProvider: editing } : cfg?.fallbackProvider === editing ? { fallbackProvider: null } : {}),
        dailyRequestsPerOrg: Number(form.daily) || undefined,
        providers: { [editing]: { enabled: form.enabled, baseUrl: form.baseUrl, model: form.model, disableReasoning: form.disableReasoning, ...(editing === 'cloudflare' ? { accountId: form.accountId || null, gatewayId: form.gatewayId || null } : {}), ...(form.apiKey ? { apiKey: form.apiKey } : {}) } },
      }),
    });
    if (body) { setCfg(body); setForm((f) => ({ ...f, apiKey: '' })); toast.success('Provedor de IA salvo'); }
  };

  const fetchModels = async () => {
    const body = await req('models', '/api/ai/config/models', { method: 'POST', body: JSON.stringify({ provider: editing, apiKey: form.apiKey || undefined, baseUrl: form.baseUrl || undefined, accountId: form.accountId || undefined }) });
    if (body) { setModels(body.models); toast.success(`${body.models.length} modelos encontrados`); }
  };

  const test = async () => {
    const body = await req('test', '/api/ai/config/test', { method: 'POST', body: JSON.stringify({ provider: editing }) });
    if (body) toast.success(`Respondeu em ${(body.ms / 1000).toFixed(1)} s com ${body.model}`);
  };

  return (
    <>
      <div className="settings-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <span style={{ height: 28, display: 'flex', alignItems: 'center', color: active ? 'var(--text)' : 'var(--purple-text)' }}>
              {/* "OpenAI-compatible" (ex.: DGX) não tem marca: mostra a do fabricante do modelo */}
              <ProviderLogo id={active ? (active.id === 'compatible' ? modelVendor(active.model) : active.id) : null} size={24} />
            </span>
            {active ? <Badge variant="green">Ativo</Badge> : <Badge variant="neutral">Não configurado</Badge>}
          </div>
          <div className="font-semibold" style={{ marginTop: 12 }}>Provedores de IA</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Assistente do painel e IA padrão das campanhas</div>
          <div className="integration-host" title={active?.baseUrl || undefined}>
            {active && modelVendor(active.model) ? <ProviderLogo id={modelVendor(active.model)} size={14} /> : <ICONS.Key size={16} />}
            <span>{active ? `${active.label} · ${active.model}` : 'sem provedor padrão'}</span>
          </div>
        </div>
        <div className="settings-card__actions">
          <Button variant="secondary" icon={active ? ICONS.Edit : ICONS.Plus} onClick={() => { pick(cfg?.defaultProvider || 'compatible'); setOpen(true); }}>
            {active ? 'Editar' : 'Configurar'}
          </Button>
        </div>
      </div>

      <SidePanel
        open={open}
        onClose={() => setOpen(false)}
        title="Provedores de IA"
        description="Assistente do painel e IA padrão das campanhas. Escolha o provedor e ajuste a conexão."
        formId="ai-providers-form"
        submitting={busy === 'save'}
        footerStart={cfg && current ? (
          <>
            <Button variant="secondary" icon={ICONS.MagnifyingGlass} type="button" loading={busy === 'models'} onClick={fetchModels}>Buscar modelos</Button>
            <Button variant="secondary" icon={ICONS.Play} type="button" loading={busy === 'test'} disabled={!current.enabled} title={current.enabled ? undefined : 'Salve e ative antes de testar'} onClick={test}>Testar</Button>
          </>
        ) : undefined}
      >
        {cfg && current && (
          <form id="ai-providers-form" noValidate onSubmit={save} className="flex flex-col gap-5">
            <div className="ai-providers">
              {cfg.providers.map((p) => (
                <button key={p.id} type="button" className={`ai-providers__item ${p.id === editing ? 'is-selected' : ''}`} onClick={() => pick(p.id)}>
                  <strong className="ai-providers__name"><ProviderLogo id={p.id === 'compatible' ? null : p.id} size={16} />{p.label}</strong>
                  <span>{cfg.defaultProvider === p.id && p.enabled ? 'Padrão' : cfg.fallbackProvider === p.id && p.enabled ? 'Reserva' : p.enabled ? 'Ativo' : p.hasKey ? 'Desligado' : 'Não configurado'}</span>
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              {current.needsBaseUrl && (
                <Field label="Endereço" description="Endereço do servidor OpenAI-compatible; o /v1 é incluído se faltar">
                  <Input onChange={(e) => setForm({ ...form, baseUrl: e.target.value.trim() })} value={form.baseUrl} />
                </Field>
              )}
              {editing === 'cloudflare' && (
                <>
                  <Field label="Account ID" description="Painel da Cloudflare › Workers AI (ou na URL do painel)">
                    <Input onChange={(e) => setForm({ ...form, accountId: e.target.value.trim() })} value={form.accountId} />
                  </Field>
                  <Field label="AI Gateway (opcional)" description="Nome do gateway: logs, cache e limites de uso das chamadas">
                    <Input onChange={(e) => setForm({ ...form, gatewayId: e.target.value.trim() })} value={form.gatewayId} />
                  </Field>
                </>
              )}
              <PasswordField
                label={current.needsBaseUrl ? 'Token (Authorization: Bearer)' : editing === 'cloudflare' ? 'API token (permissão Workers AI)' : 'Chave de API'}
                placeholder={current.hasKey ? 'Deixe em branco para manter a atual' : ''}
                value={form.apiKey}
                onChange={(value) => setForm({ ...form, apiKey: value.trim() })}
                autoComplete="off"
              />
              <div>
                <Field label="Modelo">
                  <Input onChange={(e) => setForm({ ...form, model: e.target.value.trim() })} value={form.model} />
                </Field>
                {models.length > 0 && (
                  <div className="ai-models">
                    {models.map((m) => (
                      <button key={m} type="button" className={m === form.model ? 'is-selected' : ''} onClick={() => setForm({ ...form, model: m })}>
                        {modelVendor(m) && <ProviderLogo id={modelVendor(m)} size={12} />}{m}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Field label="Limite de pedidos por organização por dia">
                <Input onChange={(e) => setForm({ ...form, daily: e.target.value })} type="number" value={form.daily} />
              </Field>
            </div>

            <div className="flex flex-col gap-2">
              <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, enabled: checked })} checked={form.enabled} label={<>Ativo</>} />
              <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, isDefault: checked })} checked={form.isDefault} label={<>Provedor padrão da plataforma (assistente e IA das campanhas)</>} />
              <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, isFallback: checked })} checked={form.isFallback && !form.isDefault} disabled={form.isDefault} label={<>Reserva: usar quando o padrão estiver fora do ar ou lento</>} />
              <Checkbox onCheckedChange={(checked: boolean) => setForm({ ...form, disableReasoning: checked })} checked={form.disableReasoning} label={<>Desligar o raciocínio (thinking) dos modelos: respostas bem mais rápidas</>} />
            </div>

          </form>
        )}
      </SidePanel>
    </>
  );
}
